import { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, SpaceBetween } from '@amzn/awsui-components-core';

import styles from './PopoverDialog.module.scss';

interface PopoverDialogProps {
  /** The bounding rectangle of the target element the dialog points to. */
  readonly target: DOMRect;
  /** Icon displayed in the header next to the title. */
  readonly icon: React.ReactNode;
  /** Title displayed in the header next to the icon. */
  readonly title: string;
  /** Description content displayed in the dialog body. */
  readonly description: React.ReactNode;
  /** Called when the dialog is dismissed (close button clicked). */
  readonly onClose: () => void;
  /** Called when the primary "Accept" button is clicked. Only needed in accept_reject mode. */
  readonly onAccept?: () => void;
  /** Called when the "Reject" button is clicked. Only needed in accept_reject mode. */
  readonly onReject?: () => void;
  /** Whether left navigation is available. */
  readonly isNavigateLeftEnabled: boolean;
  /** Whether right navigation is available. */
  readonly isNavigateRightEnabled: boolean;
  /** Called when the left arrow navigation button is clicked. */
  readonly onNavigateLeft: () => void;
  /** Called when the right arrow navigation button is clicked. */
  readonly onNavigateRight: () => void;
  /** Whether the Accept button is enabled. Defaults to true. */
  readonly isAcceptEnabled?: boolean;
  /** Whether the Reject button is enabled. Defaults to true. */
  readonly isRejectEnabled?: boolean;
  /** Controls the footer button layout. Defaults to 'accept_reject'. */
  readonly mode?: 'accept_reject' | 'edit';
  /** Called when the "Edit" button is clicked (only used in 'edit' mode). */
  readonly onEdit?: () => void;
}

interface Position {
  readonly top: number;
  readonly left: number;
  readonly arrowLeft: number;
  readonly placement: 'top' | 'bottom';
}

/**
 * Positions a dialog relative to a target rectangle in page (absolute) coordinates.
 *
 * Placement: prefers above the target; falls back to below when there is no room above.
 * Horizontal: centers on the target, then clamps to page edges with a 4px gutter.
 * Arrow: always points at the target center, clamped 16px from the dialog's left/right edges
 * so it never overflows the rounded corners.
 */
function calculatePosition(targetRect: DOMRect, dialog: HTMLElement): Position {
  const dialogRect = dialog.getBoundingClientRect();
  const targetCenterX = targetRect.left + targetRect.width / 2;

  const arrowHeight = 8;
  const dialogWidth = dialogRect.width;
  const dialogHeight = dialogRect.height;

  // Prefer placing above; fall back to below when it would go off-screen
  let placement: 'top' | 'bottom' = 'top';
  let top = targetRect.top - dialogHeight - arrowHeight;

  if (top < 0) {
    placement = 'bottom';
    top = targetRect.bottom + arrowHeight;
  }

  // Center on target, then clamp to page edges
  let left = targetCenterX - dialogWidth / 2;

  const pageWidth = document.documentElement.scrollWidth;
  const minLeft = 4;
  const maxLeft = pageWidth - dialogWidth - 4;

  if (left < minLeft) {
    left = minLeft;
  } else if (left > maxLeft) {
    left = maxLeft;
  }

  // Arrow offset relative to the dialog's left edge, clamped away from corners
  const arrowLeft = Math.max(16, Math.min(targetCenterX - left, dialogWidth - 16));

  return { top, left, arrowLeft, placement };
}

const PopoverDialog = memo(function PopoverDialog({
  target,
  icon,
  title,
  description,
  onClose,
  onAccept,
  onReject,
  isNavigateLeftEnabled,
  isNavigateRightEnabled,
  onNavigateLeft,
  onNavigateRight,
  isAcceptEnabled = true,
  isRejectEnabled = true,
  mode = 'accept_reject',
  onEdit,
}: PopoverDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  // When target changes, recalculate where to place the dialog
  useEffect(() => {
    if (!dialogRef.current) return;
    setPosition(calculatePosition(target, dialogRef.current));
  }, [target]);

  // Dismiss when clicking outside the dialog
  useEffect(() => {
    function handleMouseDown(event: MouseEvent) {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  return createPortal(
    <div
      ref={dialogRef}
      className={styles.overlay}
      style={
        position
          ? { top: position.top, left: position.left }
          : { visibility: 'hidden', top: 0, left: 0 }
      }
    >
      <div className={styles.container}>
        {/* Arrow */}
        <div
          className={`${styles.arrow} ${position?.placement === 'top' ? styles.arrowTop : styles.arrowBottom}`}
          style={{ left: position ? position.arrowLeft - 6 : '50%' }}
        />

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {icon}
            <span className={styles.title}>{title}</span>
          </div>
          <Button variant="icon" iconName="close" ariaLabel="Close" onClick={onClose} />
        </div>

        {/* Body */}
        <div className={styles.body}>{description}</div>

        {/* Footer */}
        <div className={styles.footer}>
          {mode === 'accept_reject' && onAccept && (
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="normal" onClick={onAccept} disabled={!isAcceptEnabled}>
                Accept
              </Button>
              <Button variant="link" onClick={onReject} disabled={!isRejectEnabled}>
                Reject
              </Button>
            </SpaceBetween>
          )}
          {mode === 'edit' && (
            <Button variant="normal" onClick={onEdit}>
              Edit
            </Button>
          )}
          <div className={styles.navigationButtons}>
            <Button
              variant="icon"
              iconName="angle-left"
              ariaLabel="Previous"
              disabled={!isNavigateLeftEnabled}
              onClick={onNavigateLeft}
            />
            <Button
              variant="icon"
              iconName="angle-right"
              ariaLabel="Next"
              disabled={!isNavigateRightEnabled}
              onClick={onNavigateRight}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
});

export { PopoverDialog, calculatePosition };
export type { PopoverDialogProps, Position };
